import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  inviteDirector,
  listDirectors,
  removeDirector,
  updateDirector,
  type DirectorSummary,
} from '../lib/api/admin';
import { showErrorToast, showSuccessToast } from '../lib/toast';
import { ApiError } from '../lib/api/core';
import {
  InviteLocaleSelect,
  useDefaultInviteLocale,
} from '../components/InviteLocaleSelect';

// Story 063 — director management dashboard. Org-admin-only, gated by
// AdminRoute. Lists directors of the current tenant with invite + remove
// actions. Removal is blocked at the backend when the director still
// owns non-completed tournaments; the 409 surfaces as an informative
// error toast.

export function AdminDirectorsPage() {
  const { t } = useTranslation('director');
  const defaultPickerLocale = useDefaultInviteLocale();
  const [directors, setDirectors] = useState<DirectorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteLocale, setInviteLocale] = useState<string>(defaultPickerLocale);
  const [inviting, setInviting] = useState(false);
  const [confirmingUserId, setConfirmingUserId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const list = await listDirectors();
      setDirectors(list);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.userMessage
          : t('admin.directors.toast.load_failed');
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // t intentionally omitted — stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    const email = inviteEmail.trim();
    const name = inviteName.trim();
    if (!email || !name) return;
    setInviting(true);
    try {
      await inviteDirector(email, name, inviteLocale);
      showSuccessToast(t('admin.directors.toast.invited', { email }));
      setInviteEmail('');
      setInviteName('');
      setInviteLocale(defaultPickerLocale);
      setShowInviteForm(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.userMessage
          : t('admin.directors.toast.invite_failed');
      showErrorToast(message);
    } finally {
      setInviting(false);
    }
  }

  function startEdit(d: DirectorSummary) {
    setEditingUserId(d.id);
    setEditingName(d.name);
  }

  function cancelEdit() {
    setEditingUserId(null);
    setEditingName('');
  }

  async function handleSaveEdit(d: DirectorSummary) {
    const name = editingName.trim();
    if (!name) return;
    setSavingEdit(true);
    try {
      const updated = await updateDirector(d.id, { name });
      setDirectors((prev) =>
        prev.map((x) => (x.id === d.id ? { ...x, name: updated.name } : x)),
      );
      showSuccessToast(t('admin.directors.toast.updated'));
      cancelEdit();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.userMessage
          : t('admin.directors.toast.update_failed');
      showErrorToast(message);
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleRemove(d: DirectorSummary) {
    setRemoving(d.id);
    try {
      await removeDirector(d.id);
      showSuccessToast(t('admin.directors.toast.removed', { email: d.email }));
      setDirectors((prev) => prev.filter((x) => x.id !== d.id));
      setConfirmingUserId(null);
    } catch (err) {
      // 409 from the backend means the director still owns active
      // tournaments — surface the count + reason from the response body.
      const message =
        err instanceof ApiError
          ? err.message
          : t('admin.directors.toast.remove_failed');
      showErrorToast(message);
    } finally {
      setRemoving(null);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{t('admin.directors.title')}</h1>
          <Button
            data-testid="invite-director-button"
            onClick={() => setShowInviteForm((s) => !s)}
          >
            {showInviteForm
              ? t('admin.directors.cancel_button')
              : t('admin.directors.invite_button')}
          </Button>
        </div>

        {showInviteForm && (
          <form
            onSubmit={handleInvite}
            className="mb-6 space-y-3 rounded border bg-card p-4"
          >
            <div>
              <label className="mb-1 block text-sm font-medium">{t('admin.directors.invite_form.name_label')}</label>
              <Input
                required
                placeholder={t('admin.directors.invite_form.name_placeholder')}
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                data-testid="invite-name-input"
                disabled={inviting}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t('admin.directors.invite_form.email_label')}</label>
              <Input
                type="email"
                required
                placeholder={t('admin.directors.invite_form.email_placeholder')}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
                disabled={inviting}
              />
            </div>
            <InviteLocaleSelect
              value={inviteLocale}
              onChange={setInviteLocale}
              disabled={inviting}
              labelText={t('admin.directors.invite_form.language_label')}
              hintText={t('admin.directors.invite_form.language_hint')}
            />
            <Button
              type="submit"
              data-testid="invite-submit"
              disabled={inviting}
            >
              {inviting
                ? t('admin.directors.invite_form.submitting')
                : t('admin.directors.invite_form.submit')}
            </Button>
          </form>
        )}

        {loading ? (
          <p className="text-muted-foreground">{t('admin.directors.loading')}</p>
        ) : directors.length === 0 ? (
          <p
            data-testid="director-list-empty"
            className="text-muted-foreground"
          >
            {t('admin.directors.empty')}
          </p>
        ) : (
          <ul className="divide-y rounded border">
            {directors.map((d) => (
              <li
                key={d.id}
                data-testid={`director-row-${d.email}`}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  {editingUserId === d.id ? (
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      data-testid={`edit-director-name-input-${d.email}`}
                      disabled={savingEdit}
                      autoFocus
                    />
                  ) : (
                    <p
                      data-testid={`director-name-${d.email}`}
                      className="text-sm font-medium truncate"
                    >
                      {d.name}
                    </p>
                  )}
                  <p className="font-mono text-xs text-muted-foreground truncate">
                    {d.email}
                  </p>
                </div>
                {editingUserId === d.id ? (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      data-testid="save-director-name"
                      disabled={savingEdit || !editingName.trim()}
                      onClick={() => handleSaveEdit(d)}
                    >
                      {savingEdit
                        ? t('admin.directors.saving')
                        : t('admin.directors.save_button')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="cancel-edit-director"
                      onClick={cancelEdit}
                      disabled={savingEdit}
                    >
                      {t('admin.directors.cancel_edit')}
                    </Button>
                  </div>
                ) : confirmingUserId === d.id ? (
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      data-testid="confirm-remove-director"
                      disabled={removing === d.id}
                      onClick={() => handleRemove(d)}
                    >
                      {removing === d.id
                        ? t('admin.directors.removing')
                        : t('admin.directors.confirm_remove')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmingUserId(null)}
                      disabled={removing === d.id}
                    >
                      {t('admin.directors.cancel_button')}
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="edit-director-button"
                      onClick={() => startEdit(d)}
                    >
                      {t('admin.directors.edit_button')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="remove-director-button"
                      onClick={() => setConfirmingUserId(d.id)}
                    >
                      {t('admin.directors.remove_button')}
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
